import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Skeleton } from '@heroui/react';

import AuthLayout, { AuthLink } from '../components/common/AuthLayout';
import Notice from '../components/common/Notice';
import PasswordField from '../components/common/PasswordField';
import userService from '../services/auth.service';
import useValidation from '../components/common/useValidation';
import * as check from '../services/validation';
import { messageOf } from '../i18n/el';

// Δεύτερο βήμα της ανάκτησης πρόσβασης: ορισμός νέου κωδικού από σύνδεσμο με
// διακριτικό (ΛΑ-01).
//
// **Η οθόνη υπάρχει και η διαδρομή του API όχι** — βλ. `ResetPasswordPage.jsx`.
// Η φόρμα δεν εμφανίζεται όσο ο έλεγχος του διακριτικού είναι σε εξέλιξη, ώστε να
// μη φαίνεται στιγμιαία σε άκυρο σύνδεσμο.

export default function SetNewPasswordPage() {
    const { token } = useParams();

    const [user, setUser] = useState('');
    const [password, setPassword] = useState('');
    const [cpassword, setCpassword] = useState('');
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    // `null` όσο ο έλεγχος του διακριτικού είναι σε εξέλιξη, ώστε να μη φαίνεται
    // στιγμιαία η φόρμα σε άκυρο σύνδεσμο.
    const [validToken, setValidToken] = useState(null);

    const form = useValidation();

    // Ο έλεγχος του διακριτικού γινόταν μέσα στον **constructor** της κλάσης, δηλαδή
    // παρενέργεια σε συνάρτηση που το React μπορεί να καλέσει περισσότερες από μία
    // φορά. Μεταφέρεται σε `useEffect`, όπου ανήκει.
    //
    // Ο παλαιός χειρισμός αποτυχίας ήταν `window.location.replace(origin)`: **σιωπηλή
    // ανακατεύθυνση στην αρχική**. Όταν η διαδρομή επαλήθευσης έπαψε να επιστρέφει
    // `{ status: 400 }` και άρχισε να απορρίπτει με 501, η οθόνη σταμάτησε να
    // αποδίδεται εντελώς — χωρίς σφάλμα και χωρίς μήνυμα, ο χρήστης απλώς βρισκόταν
    // στην αρχική. Τώρα η αποτυχία εμφανίζεται ως μήνυμα και η φόρμα δεν αποδίδεται.
    useEffect(() => {
        let cancelled = false;

        userService.validateResetToken(token).then(
            (response) => {
                if (cancelled) return;
                setValidToken(true);
                setUser(response.user);
            },
            (apiError) => {
                if (cancelled) return;
                setValidToken(false);
                setError(messageOf(apiError));
            }
        );

        return () => { cancelled = true; };
    }, [token]);

    // Είναι **νέος** κωδικός, οπότε ισχύει ο κανόνας των 8 χαρακτήρων του API.
    const localPassword = check.password(password);
    const localConfirm = check.passwordConfirm(cpassword, password);

    const passwordMessage = form.show('password', localPassword);
    const confirmMessage = form.show('cpassword', localConfirm);

    function handleSubmit(event) {
        event.preventDefault();
        form.markSubmitted();
        setError('');

        if (!(user && validToken)) return;
        if (localPassword || localConfirm) return;

        setIsPending(true);
        userService.setNewPassword(token, password).then(
            () => {
                setSuccessMsg('Ο κωδικός σας άλλαξε.');
                setPassword('');
                setCpassword('');
                form.reset();
                setIsPending(false);
            },
            (apiError) => {
                // Ο σύνδεσμος κρίνεται **εδώ** και όχι πριν από τη φόρμα: δεν
                // υπάρχει διαδρομή προεπαλήθευσης, και δεν φτιάχτηκε επίτηδες.
                // Με την αποτυχία, η φόρμα αποσύρεται ώστε ο χρήστης να μη
                // δοκιμάζει ξανά τον ίδιο νεκρό σύνδεσμο.
                setValidToken(false);
                setError(messageOf(apiError));
                setIsPending(false);
            }
        );
    }

    return (
        <AuthLayout
            title="Νέος κωδικός"
            subtitle={validToken ? 'Επιλέξτε νέο κωδικό για τον λογαριασμό σας' : undefined}
            footer={<AuthLink to="/login">Επιστροφή στη σύνδεση</AuthLink>}
        >
            {error && <Notice status="danger" title="Ο σύνδεσμος δεν ισχύει">{error}</Notice>}
            {successMsg && (
                <Notice status="success" title="Έτοιμο" onDismiss={() => setSuccessMsg('')}>
                    {successMsg}
                </Notice>
            )}

            {validToken === null && (
                <div className="flex flex-col gap-3" aria-busy="true">
                    <Skeleton className="h-4 w-2/5 rounded" />
                    <Skeleton className="h-9 w-full rounded-xl" />
                    <Skeleton className="h-4 w-2/5 rounded" />
                    <Skeleton className="h-9 w-full rounded-xl" />
                </div>
            )}

            {validToken === true && (
                <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
                    <PasswordField
                        value={password}
                        onChange={setPassword}
                        onBlur={form.touch('password')}
                        autoComplete="new-password"
                        isInvalid={Boolean(passwordMessage)}
                        errorMessage={passwordMessage}
                    />

                    <PasswordField
                        label="Επιβεβαίωση κωδικού"
                        name="cpassword"
                        value={cpassword}
                        onChange={setCpassword}
                        onBlur={form.touch('cpassword')}
                        autoComplete="new-password"
                        isInvalid={Boolean(confirmMessage)}
                        errorMessage={confirmMessage}
                    />

                    <Button type="submit" isPending={isPending} className="mt-2">
                        Αποθήκευση
                    </Button>
                </form>
            )}
        </AuthLayout>
    );
}
